import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import MangaCard from '@/components/feature/MangaCard';
import { GENRES, getGenreById, getMangasByGenre, getHotMangas, type Manga } from '@/mocks/manga';

type StatusFilter = '全部' | '连载中' | '已完结';
type SortKey = '人气' | '评分' | '更新';

export default function CategoryPage() {
  const { genreId = '' } = useParams();
  const genre = getGenreById(genreId);
  const [status, setStatus] = useState<StatusFilter>('全部');
  const [sort, setSort] = useState<SortKey>('人气');

  const list = useMemo(() => {
    let arr = getMangasByGenre(genreId);
    if (status !== '全部') arr = arr.filter((m) => m.status === status);
    if (sort === '人气') arr = [...arr].sort((a, b) => b.views.localeCompare(a.views));
    if (sort === '评分') arr = [...arr].sort((a, b) => b.score - a.score);
    if (sort === '更新') arr = [...arr].sort((a, b) => a.chapters - b.chapters);
    return arr;
  }, [genreId, status, sort]);

  usePageMeta({
    title: genre
      ? `${genre.name}漫画推荐 - 免费51漫画${genre.name}`
      : '分类 - 免费51漫画',
    description: genre
      ? `免费51漫画${genre.name}类作品推荐，${genre.desc}，精选优质${genre.name}漫画，支持按人气、评分、更新排序筛选。`
      : '免费51漫画分类页面，按热血类、搞笑类、少女漫、少年漫、完结作品、汉化组标签筛选漫画。',
    path: `/category/${genreId}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: genre ? `${genre.name}漫画推荐` : '漫画分类',
        url: `https://shonenx.github.io/category/${genreId}`,
        description: genre ? `免费51漫画${genre.name}类作品合集。` : '免费51漫画分类页面。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://shonenx.github.io/' },
          { '@type': 'ListItem', position: 2, name: genre ? genre.name : '分类', item: `https://shonenx.github.io/category/${genreId}` },
        ],
      },
    ],
  });

  if (!genre) {
    return (
      <main className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <i className="ri-file-unknow-line text-5xl text-foreground-400"></i>
        <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">未找到该分类</h1>
        <Link
          to="/"
          className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </main>
    );
  }

  const otherGenres = GENRES.filter((g) => g.id !== genreId);
  const hot = getHotMangas(5);

  return (
    <main className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav className="text-sm text-foreground-600" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              <li aria-current="page" className="font-medium text-foreground-900">{genre.name}漫画</li>
            </ol>
          </nav>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary-500 text-2xl text-background-50">
              <i className={genre.icon}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                免费51漫画 · {genre.name}类
              </h1>
              <p className="mt-1 text-sm text-foreground-600">{genre.desc}，共 {list.length} 部作品。</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          <div className="lg:col-span-3">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2 rounded-full border border-background-200 bg-background-100 p-1">
                {(['全部', '连载中', '已完结'] as StatusFilter[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatus(s)}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                      status === s ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:bg-secondary-100'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-600">排序</span>
                <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                  {(['人气', '评分', '更新'] as SortKey[]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSort(s)}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        sort === s ? 'bg-secondary-500 text-background-50' : 'text-foreground-700 hover:bg-secondary-100'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {list.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {list.map((m: Manga) => (
                  <MangaCard key={m.id} manga={m} />
                ))}
              </div>
            ) : (
              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-10 text-center">
                <i className="ri-inbox-line text-4xl text-foreground-400"></i>
                <p className="mt-3 text-sm text-foreground-600">该筛选条件下暂无作品，试试切换条件。</p>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">其他分类</h2>
              <ul className="mt-4 space-y-2">
                {otherGenres.map((g) => (
                  <li key={g.id}>
                    <Link
                      to={`/category/${g.id}`}
                      className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-50 hover:text-primary-700"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${g.icon} text-primary-600`}></i>
                        {g.name}漫画
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">全站热门</h2>
              <ol className="mt-4 space-y-3">
                {hot.map((m, i) => (
                  <li key={m.id}>
                    <Link
                      to={`/manga/${m.id}`}
                      className="group flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-background-50"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold text-background-50 ${
                          i < 3 ? 'bg-primary-500' : 'bg-secondary-400'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="line-clamp-1 flex-1 text-sm text-foreground-800 group-hover:text-primary-700">
                        {m.title}
                      </span>
                      <span className="text-xs text-foreground-500">{m.views}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}